// @ts-nocheck
import React, { useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, MapPin, Loader2, Plus, Trash2, RefreshCw, Navigation,
} from "lucide-react";
import AvailabilityTab from "@/components/events/AvailabilityTab";
import CourtSetupTools from "@/components/events/CourtSetupTools";


const UNASSIGNED = "__unassigned__";

const venueAddress = (v: any) =>
  [v?.address, v?.city, v?.state, v?.zip_code].filter(Boolean).join(", ") || "No address on record";

export default function OperatorEventVenuesCourtsPage() {
  // Deep links (e.g. from the Schedule draft page) can open a specific tab.
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab") ?? "";
  const initialTab = ["venues", "courts", "availability", "travel"].includes(tabParam)
    ? tabParam
    : "venues";
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [busy, setBusy] = useState(false);
  const [newCourtName, setNewCourtName] = useState("");
  const [addVenueId, setAddVenueId] = useState("");

  /* ---------------- data ---------------- */
  const eventQuery = useQuery({
    queryKey: ["vc-event", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .select("id, name, start_date, organization_id, main_venue_id, additional_venue_ids")
        .eq("id", eventId)
        .single();
      if (error) throw error;
      return data;
    },
  });
  const event = eventQuery.data;

  const eventVenueIds = useMemo(() => {
    if (!event) return [];
    return Array.from(
      new Set([event.main_venue_id, ...((event.additional_venue_ids as string[]) ?? [])].filter(Boolean)),
    );
  }, [event]);

  const venuesQuery = useQuery({
    queryKey: ["vc-venues", eventVenueIds],
    enabled: eventVenueIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("venues")
        .select("id, name, address, city, state, zip_code, latitude, longitude")
        .in("id", eventVenueIds);
      if (error) throw error;
      // preserve main-first ordering
      return eventVenueIds.map((id) => data.find((v: any) => v.id === id)).filter(Boolean);
    },
  });
  const venues = venuesQuery.data ?? [];

  const orgVenuesQuery = useQuery({
    queryKey: ["vc-org-venues", event?.organization_id],
    enabled: !!event,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("venues")
        .select("id, name, city, state, is_active")
        .order("name");
      if (error) throw error;
      return (data ?? []).filter((v: any) => v.is_active !== false);
    },
  });

  const courtsQuery = useQuery({
    queryKey: ["vc-courts", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("event_courts")
        .select("id, name, venue_id, is_active, sort_order, notes")
        .eq("event_id", eventId)
        .order("sort_order");
      if (error) throw error;
      return (data ?? []).filter((c: any) => c.is_active !== false);
    },
  });
  const courts = courtsQuery.data ?? [];

  const travelQuery = useQuery({
    queryKey: ["vc-travel", eventId, eventVenueIds.length],
    enabled: !!eventId && eventVenueIds.length > 1,
    queryFn: async () => {
      const { data, error } = await invokeEdgeFunction("rle-venue-travel", { body: { event_id: eventId } });
      if (error) throw new Error(error.message);
      if (data?.ok === false) throw new Error(data?.error?.message || "Could not load travel times");
      return data;
    },
  });

  /* ---------------- actions ---------------- */
  const addVenue = async () => {
    if (!addVenueId) return;
    setBusy(true);
    try {
      const next = Array.from(new Set([...((event.additional_venue_ids as string[]) ?? []), addVenueId]));
      const { error } = await supabase
        .from("events")
        .update({ additional_venue_ids: next })
        .eq("id", eventId);
      if (error) throw error;
      setAddVenueId("");
      toast.success("Venue added to this event.");
      await qc.invalidateQueries({ queryKey: ["vc-event", eventId] });
    } catch (e: any) {
      toast.error(e.message ?? "Could not add venue");
    } finally { setBusy(false); }
  };

  const removeVenue = async (venueId: string) => {
    setBusy(true);
    try {
      const next = ((event.additional_venue_ids as string[]) ?? []).filter((id) => id !== venueId);
      const { error } = await supabase.from("events").update({ additional_venue_ids: next }).eq("id", eventId);
      if (error) throw error;
      await supabase.from("event_courts").update({ venue_id: null }).eq("event_id", eventId).eq("venue_id", venueId);
      toast.success("Venue removed from this event.");
      await qc.invalidateQueries({ queryKey: ["vc-event", eventId] });
      await qc.invalidateQueries({ queryKey: ["vc-courts", eventId] });
    } catch (e: any) {
      toast.error(e.message ?? "Could not remove venue");
    } finally { setBusy(false); }
  };

  const geocode = async (venueIds: string[]) => {
    setBusy(true);
    try {
      const { data, error } = await invokeEdgeFunction("rle-venue-geocode", { body: { venue_ids: venueIds } });
      if (error) throw new Error(error.message);
      if (data?.ok === false) throw new Error(data?.error?.message || "Geocoding failed");
      if (data.failed?.length) {
        toast.error(`Could not locate: ${data.failed.map((f: any) => `${f.name} (${f.reason})`).join("; ")}`);
      } else {
        toast.success("Coordinates saved.");
      }
      await qc.invalidateQueries({ queryKey: ["vc-venues"] });
    } catch (e: any) {
      toast.error(e.message ?? "Geocoding failed");
    } finally { setBusy(false); }
  };

  const addCourt = async () => {
    const name = newCourtName.trim();
    if (!name) return;
    setBusy(true);
    try {
      const { error } = await supabase.from("event_courts").insert({
        event_id: eventId,
        name,
        is_active: true,
        sort_order: courts.length,
        venue_id: event?.main_venue_id ?? null,
      });
      if (error) throw error;
      setNewCourtName("");
      await qc.invalidateQueries({ queryKey: ["vc-courts", eventId] });
    } catch (e: any) {
      toast.error(e.message ?? "Could not add court");
    } finally { setBusy(false); }
  };

  const patchCourt = async (id: string, patch: any) => {
    const { error } = await supabase.from("event_courts").update(patch).eq("id", id);
    if (error) { toast.error(error.message); return; }
    await qc.invalidateQueries({ queryKey: ["vc-courts", eventId] });
  };

  const removeCourt = async (id: string) => {
    if (!window.confirm("Remove this court?")) return;
    const { error } = await supabase.from("event_courts").delete().eq("id", id);
    if (error) { toast.error(error.message); return; }
    await qc.invalidateQueries({ queryKey: ["vc-courts", eventId] });
  };

  /* ---------------- derived ---------------- */
  const grouped = useMemo(() => {
    const groups = venues.map((v: any) => ({
      venue: v,
      courts: courts.filter((c: any) => c.venue_id === v.id),
    }));
    const unassigned = courts.filter((c: any) => !c.venue_id || !venues.some((v: any) => v.id === c.venue_id));
    return { groups, unassigned };
  }, [venues, courts]);

  const travelLookup = useMemo(() => {
    const map: Record<string, any> = {};
    for (const row of travelQuery.data?.matrix ?? []) {
      map[`${row.from_venue_id}|${row.to_venue_id}`] = row;
    }
    return map;
  }, [travelQuery.data]);

  if (eventQuery.isLoading) {
    return <div className="flex justify-center p-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      <div>
        <button
          onClick={() => navigate(-1)}
          className="mb-2 flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="mr-1 h-4 w-4" /> Back
        </button>
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <MapPin className="h-6 w-6" /> Venues &amp; Courts
        </h1>
        <p className="text-muted-foreground">
          {event?.name} — the buildings this event runs in, the courts inside them, and drive times between them.
        </p>
      </div>

      <Tabs defaultValue={initialTab}>
        <TabsList>
          <TabsTrigger value="venues">Venues</TabsTrigger>
          <TabsTrigger value="courts">Courts</TabsTrigger>
          <TabsTrigger value="availability">Availability</TabsTrigger>
          <TabsTrigger value="travel">Travel</TabsTrigger>
        </TabsList>


        {/* ------------- Venues ------------- */}
        <TabsContent value="venues" className="mt-4 space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
              <CardTitle className="text-base">Event venues</CardTitle>
              <div className="flex items-center gap-2">
                <Select value={addVenueId} onValueChange={setAddVenueId}>
                  <SelectTrigger className="w-64"><SelectValue placeholder="Add venue…" /></SelectTrigger>
                  <SelectContent>
                    {(orgVenuesQuery.data ?? [])
                      .filter((v: any) => !eventVenueIds.includes(v.id))
                      .map((v: any) => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.name}{v.city ? ` — ${v.city}, ${v.state ?? ""}` : ""}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={addVenue} disabled={!addVenueId || busy}>
                  <Plus className="mr-1 h-4 w-4" /> Add
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {venues.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">No venues on this event yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Venue</TableHead>
                      <TableHead>Address</TableHead>
                      <TableHead>Coordinates</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {venues.map((v: any) => {
                      const located = v.latitude != null && v.longitude != null;
                      const isMain = v.id === event?.main_venue_id;
                      return (
                        <TableRow key={v.id}>
                          <TableCell className="font-medium">
                            {v.name}{" "}
                            {isMain && <Badge variant="secondary" className="ml-1">Main venue</Badge>}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">{venueAddress(v)}</TableCell>
                          <TableCell>
                            {located
                              ? <Badge variant="outline">Located</Badge>
                              : <Badge variant="destructive">Needs geocoding</Badge>}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              {!located && (
                                <Button size="sm" variant="outline" disabled={busy} onClick={() => geocode([v.id])}>
                                  <Navigation className="mr-1 h-4 w-4" /> Geocode
                                </Button>
                              )}
                              {!isMain && (
                                <Button size="sm" variant="ghost" disabled={busy} onClick={() => removeVenue(v.id)}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ------------- Courts ------------- */}
        <TabsContent value="courts" className="mt-4 space-y-4">
          <Card>
            <CardHeader className="flex flex-col gap-3 space-y-0 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="text-base">Courts / fields</CardTitle>
              <div className="flex flex-wrap items-center gap-2">
                <CourtSetupTools
                  eventId={eventId!}
                  onDone={async () => {
                    await qc.invalidateQueries({ queryKey: ["vc-courts", eventId] });
                    await qc.invalidateQueries({ queryKey: ["vc-event", eventId] });
                  }}
                />
                <Input
                  className="w-56"
                  placeholder="New court name"
                  value={newCourtName}
                  onChange={(e) => setNewCourtName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") addCourt(); }}
                />
                <Button size="sm" onClick={addCourt} disabled={!newCourtName.trim() || busy}>
                  <Plus className="mr-1 h-4 w-4" /> Add court
                </Button>
              </div>
            </CardHeader>

            <CardContent className="space-y-6">
              {grouped.groups.map(({ venue, courts: list }: any) => (
                <div key={venue.id} className="space-y-2">
                  <h3 className="text-sm font-semibold">
                    {venue.name}
                    <span className="ml-2 text-xs font-normal text-muted-foreground">
                      {list.length} court{list.length === 1 ? "" : "s"}
                    </span>
                  </h3>
                  <CourtRows
                    list={list} venues={venues} onPatch={patchCourt} onRemove={removeCourt}
                  />
                </div>
              ))}
              {grouped.unassigned.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-muted-foreground">Unassigned</h3>
                  <CourtRows
                    list={grouped.unassigned} venues={venues} onPatch={patchCourt} onRemove={removeCourt}
                  />
                </div>
              )}
              {courts.length === 0 && (
                <p className="py-6 text-center text-sm text-muted-foreground">No courts yet.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ------------- Availability ------------- */}
        <TabsContent value="availability" className="mt-4">
          <AvailabilityTab eventId={eventId!} event={event} courts={courts} />
        </TabsContent>

        {/* ------------- Travel ------------- */}

        <TabsContent value="travel" className="mt-4 space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
              <CardTitle className="text-base">Driving time between venues</CardTitle>
              {eventVenueIds.length > 1 && (
                <Button
                  size="sm" variant="outline"
                  disabled={travelQuery.isFetching}
                  onClick={() => travelQuery.refetch()}
                >
                  <RefreshCw className={`mr-1 h-4 w-4 ${travelQuery.isFetching ? "animate-spin" : ""}`} /> Refresh
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {eventVenueIds.length <= 1 ? (
                <p className="text-sm text-muted-foreground">
                  All courts are at {venues[0]?.name ?? "this event's venue"}. No travel time to account for.
                </p>
              ) : travelQuery.isLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
              ) : travelQuery.error ? (
                <p className="text-sm text-destructive">{(travelQuery.error as Error).message}</p>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>From \ To</TableHead>
                        {venues.map((v: any) => <TableHead key={v.id}>{v.name}</TableHead>)}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {venues.map((from: any) => (
                        <TableRow key={from.id}>
                          <TableCell className="font-medium">{from.name}</TableCell>
                          {venues.map((to: any) => {
                            if (from.id === to.id) return <TableCell key={to.id} className="text-muted-foreground">—</TableCell>;
                            const row = travelLookup[`${from.id}|${to.id}`];
                            return (
                              <TableCell key={to.id}>
                                {row ? `${Math.round(row.driving_seconds / 60)} min` : "—"}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {travelQuery.data?.geocode_failures?.length > 0 && (
                    <p className="mt-3 text-sm text-destructive">
                      Could not locate:{" "}
                      {travelQuery.data.geocode_failures.map((f: any) => `${f.name} (${f.reason})`).join("; ")}
                    </p>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------- court rows ---------------- */
const CourtRows = ({ list, venues, onPatch, onRemove }: any) => (
  <div className="space-y-2">
    {list.map((c: any) => (
      <div key={c.id} className="flex items-center gap-2">
        <Input
          className="max-w-xs"
          defaultValue={c.name}
          onBlur={(e) => {
            const name = e.target.value.trim();
            if (name && name !== c.name) onPatch(c.id, { name });
          }}
        />
        <Select
          value={c.venue_id ?? UNASSIGNED}
          onValueChange={(val) => onPatch(c.id, { venue_id: val === UNASSIGNED ? null : val })}
        >
          <SelectTrigger className="w-64"><SelectValue placeholder="Venue" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
            {venues.map((v: any) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" variant="ghost" onClick={() => onRemove(c.id)}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    ))}
    {list.length === 0 && <p className="text-sm text-muted-foreground">No courts here yet.</p>}
  </div>
);
